import React from 'react';

import clsx from 'clsx';
import PropTypes from 'prop-types';
import {connect} from 'react-redux';
import {
  Field,
  reduxForm,
  getFormValues,
} from 'redux-form';

import {FlexGridCellTemplate, FlexGridColumnGroup} from '@grapecity/wijmo.react.grid';

import {Box, Grid} from '@material-ui/core';
import RotateLeftIcon from '@material-ui/icons/RotateLeft';
import SearchIcon from '@material-ui/icons/Search';
import {withStyles} from '@material-ui/core/styles';

import BackButton from '../../../atoms/Buttons/BackButton';
import DangerButton from '../../../atoms/Buttons/DangerButton';
import NegativeButton from '../../../atoms/Buttons/NegativeButton';
import PositiveButton from '../../../atoms/Buttons/PositiveButton';
import PaperPart from '../../../atoms/PaperPart';

import CustomFlexGrid from '../../../molecules/CustomFlexGrid';
import MainContainer from '../../../organisms/MainContainer';

import {
  renderSelect,
  renderTextField,
} from '../../../atoms/CustomPart';

import {
  createSearchList,
} from '../../../../common/ComboParts';

import {
  API_CODE,
  changeDateFormat,
  convertCommaSeparated,
  filterValidNWZgsyo,
  getDataFromGeneralPurposeMap,
  getResponseResult,
} from '../../../../common/common';

import {drSideKoziCostOperations} from '../../../../reducks/drSideKoziCost';

import * as validateRule from '../../../validateRule';
import {MANUALLY_ADDED} from '../Detail/data';
import {isDrSideKoziCostAdmin} from '..';

const styles = (theme) => ({
  fields: {
    margin: '0px 10px 15px 10px',
  },
  button: {
    margin: '5px 10px 5px 10px',
  },
  dateFromTo: {
    display: 'flex',
    alignItems: 'center',
    margin: '0 12px',
    width: '97%',
  },
  dateWave: {
    margin: '0 10px',
  },
  searchButtons: {
    display: 'flex',
    justifyContent: 'flex-end',
  },
});

const mapStateToProps = (state) => {
  const gm = state.common.generalPurposeMap;
  const userInfo = state.auth.userInfo;

  // ユーザの所属するNW事業所を初期選択値にする
  let nwZgsyo = userInfo.Account?.NWZgsyo__r?.Code__c;

  // ユーザのNW事業所が未設定の場合、一覧の先頭項目を初期選択値にする
  if (!nwZgsyo) {
    const validNWZgsyo = filterValidNWZgsyo(gm.NWZgsyo);
    nwZgsyo = validNWZgsyo?.[0]?.Code__c;
  }

  return {
    generalPurposeMap: gm,
    initialValues: {
      nwZgsyo: nwZgsyo,
      calculationStatus: '1',
    },
  };
};

const mapDispatchToProps = {
  /** 改造工事費用算定件名一覧 取得 */
  getList: drSideKoziCostOperations.getList,

  /** 改造工事費用算定件名 Id設定 */
  setId: drSideKoziCostOperations.setId,

  /** 改造工事費用算定件名 削除 */
  deleteItems: drSideKoziCostOperations.deleteItems,
};

/**
 * 改造工事費用算定対象件名一覧 Template
 * 画面ID:1701
 *
 * @param {object} props
 * @return {JSX.Element} 改造工事費用算定対象件名一覧画面
 */
const Template = (props) => {
  const {
    classes,
    generalPurposeMap,
    reset,
    handleSubmit,
    userInfo,
    showMessage,

    history,

    getList,
    setId,
    deleteItems,

    values,
  } = props;

  const gridRef = React.useRef();

  // 画面表示時・終了時処理
  React.useEffect(() => {
    const grid = gridRef.current;

    // 表示時に一覧をクリアする
    if (grid) {
      gridRef.current.setItems([]);
    }

    // 終了時にに一覧をクリアする
    return () => {
      if (grid) {
        grid.saveScreenState('1701', 'Id');
        grid.setItems([]);
      }
    };
  }, []);

  const searchItems = React.useCallback(async () => {
    // 未入力の項目を除外したものを検索条件とする
    const conditions = Object.fromEntries(
        Object.entries(values).filter(([, v]) => v !== undefined && v !== null && v !== ''),
    );

    // 検索実行
    const response = await getList(conditions, '改造工事費用算定', '取得');
    const result = getResponseResult(response);

    // エラー時にはメッセージを表示して終了
    if (result.errorCode != API_CODE.SUCCESS) {
      showMessage({
        message: result.messages,
      });

      return;
    }

    // 取得結果、データ長を保持
    const data = response.data.body.data ?? [];
    const dataLength = data.length;

    // 一覧の最大件数を取得
    const maxKensu = getDataFromGeneralPurposeMap(
        generalPurposeMap, 'DisplayMaxKensu1', 1).ValueNum1__c;

    // このAPIは最大件数+1件まで取得するため、超過しているかどうかは正確に判定できる
    const isOverMaxKensu = dataLength > maxKensu;

    if (isOverMaxKensu) {
      // 検索結果が最大件数を超える場合はメッセージを表示
      showMessage({
        message: 'CE0015',
      });
    } else if (dataLength === 0) {
      // 検索結果が0件の場合はメッセージを表示
      showMessage({
        message: 'CE0016',
      });
    }

    // 最大件数を超える場合は、一覧には最大件数分のみ表示する(超過確認用の末尾1件は表示しない)
    const displayData = isOverMaxKensu ? data.slice(0, maxKensu) : data;

    // 取得結果を一覧へ設定
    if (gridRef.current) {
      gridRef.current.setInitItems('1701', displayData);
    }
  }, [
    // 変数
    generalPurposeMap,
    values,

    // 関数
    getList,
    showMessage,
  ]);

  const comboItems = React.useMemo(() => {
    // NW事業所
    const gmZgsyo = filterValidNWZgsyo(generalPurposeMap.NWZgsyo);

    const zgsyo = createSearchList(
        gmZgsyo, 'Code__c', 'Name', null, true);

    // 共架種別
    const kyogaType = createSearchList(generalPurposeMap.KyogaType, 'Code__c', 'Name');

    // 算定ステータス
    const status = createSearchList(generalPurposeMap.CalculationStatus, 'Code__c', 'Name');

    // 手動追加
    const manuallyAdded = createSearchList(MANUALLY_ADDED, 'Code__c', 'Name');

    return {
      zgsyo,
      kyogaType: kyogaType,
      status,
      manuallyAdded,
    };
  }, [
    // 変数
    generalPurposeMap,
  ]);

  /** 検索条件セクション */
  const conditionsSection = React.useMemo(() => {
    return (
      <PaperPart>
        <Grid
          key="search-conditions"
          container
          justifyContent="flex-start"
          alignItems="flex-start"
          spacing={1}
        >

          <Grid key="search-orderNo" item xs={12} sm={3}>
            <Field
              name="orderNo"
              className={classes.fields}
              component={renderTextField}
              label="申込番号"
              fullWidth
              type="text"
              required={false}
              inputProps={{
                maxLength: 12,
              }}
              validate={[
                validateRule.alphaNumericSymbol,
                validateRule.maxLength(12),
              ]}
            />
          </Grid>

          <Grid key="search-gaisanDesignTanto" item xs={12} sm={3}>
            <Field
              name="gaisanDesignTanto"
              className={classes.fields}
              component={renderTextField}
              label="設計担当者"
              fullWidth
              type="text"
              required={false}
              inputProps={{
                maxLength: 20,
              }}
              validate={[
                validateRule.maxLength(20),
              ]}
            />
          </Grid>

          <Grid key="search-designDocumentNo" item xs={12} sm={3}>
            <Field
              name="designDocumentNo"
              className={classes.fields}
              component={renderTextField}
              label="代表設計書番号"
              fullWidth
              type="text"
              required={false}
              inputProps={{
                maxLength: 10,
              }}
              validate={[
                validateRule.maxLength(10),
              ]}
            />
          </Grid>

          <Grid key="search-nwZgsyo" item xs={12} sm={3}>
            <Field
              name="nwZgsyo"
              className={classes.fields}
              component={renderSelect}
              label="事業所"
              fullWidth
              required={true}
              validate={[
              ]}
            >
              {comboItems.zgsyo}
            </Field>
          </Grid>

          <Grid key="search-kyogaType" item xs={12} sm={3}>
            <Field
              name="kyogaType"
              className={classes.fields}
              component={renderSelect}
              label="共架種別"
              fullWidth
              required={false}
              validate={[
              ]}
            >
              {comboItems.kyogaType}
            </Field>
          </Grid>

          <Grid key="search-kyogaZgsya" item xs={12} sm={3}>
            <Field
              name="kyogaZgsya"
              className={classes.fields}
              component={renderTextField}
              label="共架事業者名"
              fullWidth
              type="text"
              required={false}
              inputProps={{
                maxLength: 50,
              }}
              validate={[
                validateRule.maxLength(50),
              ]}
            />
          </Grid>

          <Grid key="search-orderDate" item xs={12} sm={6} md={3}>
            <Box className={classes.dateFromTo}>
              <Field
                name="orderDateFrom"
                label="本申込日"
                component={renderTextField}
                className={classes.textFieldDate}
                required={false}
                type="date"
                InputLabelProps={{
                  shrink: true,
                }}
                validate={[
                  validateRule.date,
                ]}
              />

              <span className={classes.dateWave}>～</span>

              <Field
                name="orderDateTo"
                label={<React.Fragment />}
                component={renderTextField}
                className={classes.textFieldDate}
                required={false}
                type="date"
                InputLabelProps={{
                  shrink: true,
                }}
                validate={[
                  validateRule.date,
                ]}
              />
            </Box>
          </Grid>

          <Grid key="search-repairIraiDate" item xs={12} sm={6} md={3}>
            <Box className={classes.dateFromTo}>
              <Field
                name="repairIraiDateFrom"
                label="改修依頼日"
                component={renderTextField}
                className={classes.textFieldDate}
                required={false}
                type="date"
                InputLabelProps={{
                  shrink: true,
                }}
                validate={[
                  validateRule.date,
                ]}
              />

              <span className={classes.dateWave}>～</span>

              <Field
                name="repairIraiDateTo"
                component={renderTextField}
                label={<React.Fragment />}
                className={classes.textFieldDate}
                required={false}
                type="date"
                InputLabelProps={{
                  shrink: true,
                }}
                validate={[
                  validateRule.date,
                ]}
              />
            </Box>
          </Grid>

          <Grid key="search-manuallyAdded" item xs={12} sm={3}>
            <Field
              name="manuallyAdded"
              className={classes.fields}
              component={renderSelect}
              label="手動追加"
              fullWidth
              type="text"
              required={false}
              validate={[
              ]}
            >
              {comboItems.manuallyAdded}
            </Field>
          </Grid>

          <Grid key="search-calculationStatus" item xs={12} sm={3}>
            <Field
              name="calculationStatus"
              className={classes.fields}
              component={renderSelect}
              label="ステータス"
              fullWidth
              required={false}
              validate={[
              ]}
            >
              {comboItems.status}
            </Field>
          </Grid>

          <Grid key="search-buttons" item xs>
            <Box className={classes.searchButtons}>
              <NegativeButton
                variant="contained"
                startIcon={<RotateLeftIcon />}
                className={classes.button}
                size="large"
                onClick={reset}
              >
                <span>クリア</span>
              </NegativeButton>

              <PositiveButton
                variant="contained"
                startIcon={<SearchIcon />}
                className={clsx(classes.button)}
                size="large"
                onClick={handleSubmit(searchItems)}
              >
                <span>検索</span>
              </PositiveButton>
            </Box>
          </Grid>
        </Grid>

      </PaperPart>
    );
  }, [
    // 変数
    classes,
    comboItems,

    // 関数
    handleSubmit,
    reset,
    searchItems,
  ]);

  // チェックボックスをチェックした件数」
  const [checkedCount, setCheckedCount] = React.useState(0);

  /** チェックボックスチェック時の処理 */
  const onChangeChecked = React.useCallback(() => {
    const grid = gridRef.current;
    if (!grid) {
      return;
    }

    // チェックボックスのチェック件数を保持
    setCheckedCount(grid.selectedItems.length);
  }, []);

  /**
   * FlexGridセクション
   *
   * @return {JSX.Element}
   */
  const gridSection = React.useMemo(() => {
    const props = {
      rowHeaderType: 'check',
      filterOn: true,
      headersVisibility: 'All',
      allowDragging: 'Columns',
      counterOn: false,
      AddDeleteOn: false,
      isReadOnly: true,
      style: {height: '100%'},
      checkedFunction: onChangeChecked,
    };
    return (
      <CustomFlexGrid ref={gridRef} {...props}>
        <FlexGridColumnGroup binding="Order__r.OrderNo__c" header="申込番号" width={110} >
          <FlexGridCellTemplate cellType="Cell"
            template={(context) => {
              // 未設定の場合はハイフン
              return context.item.Order__r?.OrderNo__c ?? '－';
            }}
          />
        </FlexGridColumnGroup>

        <FlexGridColumnGroup binding="DrSideKoziDocumentNo__c" header="代表設計書番号" width={140} />
        <FlexGridColumnGroup binding="NWZgsyo__r.Name" header="事業所" width={120} />
        <FlexGridColumnGroup binding="OrderKyogaZgsya__c" header="共架事業者" width={200} />
        <FlexGridColumnGroup binding="GaisanDesignTanto__c" header="設計担当者" width={120} />
        <FlexGridColumnGroup binding="KyogaType" header="共架種別" width={250} />

        <FlexGridColumnGroup binding="Order__r.OrderDate__c" header="本申込日" dataType="Date" width={120}>
          <FlexGridCellTemplate cellType="Cell"
            template={(context) => {
              // 日付のフォーマット変換
              return changeDateFormat(context.item.Order__r?.OrderDate__c);
            }}
          />
        </FlexGridColumnGroup>

        <FlexGridColumnGroup binding="Order__r.RepairIraiDate__c" header="改修依頼日" dataType="Date" width={120}>
          <FlexGridCellTemplate cellType="Cell"
            template={(context) => {
              // 日付のフォーマット変換
              return changeDateFormat(context.item.Order__r?.RepairIraiDate__c);
            }}
          />
        </FlexGridColumnGroup>

        <FlexGridColumnGroup binding="TotalAmount__c" header="改造工事費用" width={130} >
          <FlexGridCellTemplate cellType="Cell"
            template={(context) => {
              // 未設定の場合は空文字
              const amount = context.item.TotalAmount__c;
              if (amount === null || amount === undefined) {
                return '';
              }

              // 金額をカンマ区切りに変換して末尾に円を付与
              const amountStr = convertCommaSeparated(amount) + '円';
              return amountStr;
            }}
          />
        </FlexGridColumnGroup>

        <FlexGridColumnGroup binding="ManuallyAdded__c" header="手動\n追加" width={80} align="center">
          <FlexGridCellTemplate cellType="Cell"
            template={(context) => {
              // 手動追加の場合は〇を表示
              return context.item.ManuallyAdded__c ? '〇' : '';
            }}
          />
        </FlexGridColumnGroup>

        <FlexGridColumnGroup binding="CalculationStatus" header="ステータス" width={120} />
      </CustomFlexGrid>
    );
  }, [
    // 関数
    onChangeChecked,
  ],
  );

  /** 工事補償金算定 ボタンの非活性条件 */
  const disableEdit = React.useMemo(() => {
    // 選択項目が1つだけの場合活性
    return checkedCount !== 1;
  }, [checkedCount]);

  /** 削除 ボタンの非活性条件 */
  const disableDelete = React.useMemo(() => {
    // 選択項目があれば活性
    return checkedCount === 0;
  }, [checkedCount]);

  /** 工事補償金算定 ボタン クリック時処理 */
  const calculateCompensation = React.useCallback(() => {
    const selectedItems = gridRef.current?.selectedItems;
    if (selectedItems?.length !== 1) {
      // Gridがない、または選択項目が1つでない場合は処理を中断
      return;
    }

    // 選択項目のIDを設定して詳細画面へ遷移
    setId(selectedItems[0].dataItem.Id);
    history.push({pathname: '/DrSideKoziCost/Detail'});
  }, [setId, history]);

  /** 新規作成 ボタン クリック時処理 */
  const createNew = React.useCallback(() => {
    // IDをnullに設定して詳細画面へ遷移
    setId(null);
    history.push({pathname: '/DrSideKoziCost/Detail'});
  }, [setId, history]);

  /** 削除 ボタン クリック時処理 */
  const execDelete = React.useCallback(() => {
    const selectedItems = gridRef.current?.selectedItems;
    if (!selectedItems || selectedItems.length === 0) {
      // Gridがない、または選択項目がない場合は処理を中断
      return;
    }

    const isAdmin =
      isDrSideKoziCostAdmin(userInfo, generalPurposeMap.NWDesignZgsyaCode);
    const userNWZgsyo = userInfo.Account?.NWZgsyo__r?.Code__c;

    for (const item of selectedItems) {
      // 選択項目に手動追加以外の項目がある場合はエラー
      if (!item.dataItem.ManuallyAdded__c) {
        showMessage({
          message: {
            id: 'CE0057',
            values: ['手動追加ではない項目が含まれる', '、削除'],
          },
        });

        return;
      }

      // ログインユーザの所属ではない中電NW事業所がある場合はエラー(管理者の場合はチェックしない)
      if (!isAdmin) {
        if (item.dataItem.NWZgsyo__r?.Code__c !== userNWZgsyo) {
          showMessage({
            message: {
              id: 'CE0057',
              values: ['所属していない事業所が含まれる', '、削除'],
            },
          });

          return;
        }
      }
    }

    // 削除確認メッセージを表示し、OK押下時に削除API実行
    showMessage({
      message: {
        id: 'CC0005',
        values: ['削除'],
      },
      action: async () => {
        // 削除対象情報を生成
        const deleteTargets = selectedItems.map(
            (item) => {
              const data = item.dataItem;
              return {
                Id: data.Id,
                LastModifiedDate: data.LastModifiedDate,
              };
            },
        );

        // 削除処理実装
        const response = await deleteItems(deleteTargets);
        const result = getResponseResult(response);

        // エラー時にはメッセージを表示して終了
        if (result.errorCode != API_CODE.SUCCESS) {
          showMessage({
            message: result.messages,
          });

          return;
        }

        showMessage({
          message: {
            id: 'CI0009',
            values: ['削除'],
          },
          action: async () => {
            // 再検索
            await searchItems();
          },
        });
      },
    });
  }, [
    // 変数
    userInfo,
    generalPurposeMap,

    // 関数
    deleteItems,
    showMessage,
    searchItems,
  ]);

  /**
   * フッターボタンセクション
   *
   * @return {JSX.Element}
   */
  const footerBtn = React.useMemo(() => {
    return (
      <Grid container
        justifyContent="center"
        alignItems="flex-start"
        spacing={1}
      >
        <Grid key="footer-btn1" item>
          <PositiveButton
            onClick={calculateCompensation}
            variant="contained"
            size="large"
            disabled={disableEdit}
          >
            <span>工事補償金算定</span>
          </PositiveButton>
        </Grid>

        <Grid key="footer-btn2" item>
          <PositiveButton
            onClick={createNew}
            variant="contained"
            size="large"
          >
            <span>新規作成</span>
          </PositiveButton>
        </Grid>

        <Grid key="footer-btn3" item>
          <DangerButton
            onClick={execDelete}
            variant="contained"
            size="large"
            disabled={disableDelete}
          >
            <span>削除</span>
          </DangerButton>
        </Grid>

        <Grid key="footer-back" item>
          <BackButton props={props} />
        </Grid>
      </Grid>
    );
  }, [
    // 変数
    disableDelete,
    disableEdit,
    props,

    // 関数
    calculateCompensation,
    createNew,
    execDelete,
  ]);

  return (
    <form noValidate autoComplete="off">
      <MainContainer
        props={props}
        footerBtn={footerBtn}
      >
        <Box sx={{display: 'flex', flexDirection: 'column', height: 'calc(100vh - 200px)'}}>
          {/* 検索条件セクション */}
          <Box sx={{flex: '0 0 auto'}}>
            {conditionsSection}
          </Box>

          {/* 検索結果一覧セクション */}
          <Box sx={{flex: 1, display: 'flex', flexDirection: 'column', overflow: 'auto'}}>
            <Box sx={{height: '97%'}}>
              {gridSection}
            </Box>
          </Box>
        </Box>

      </MainContainer>
    </form>
  );
};

Template.propTypes = {
  // ストア要素
  generalPurposeMap: PropTypes.object,
  userInfo: PropTypes.object.isRequired,

  // ストア操作/API実行
  getList: PropTypes.func.isRequired,
  setId: PropTypes.func.isRequired,
  deleteItems: PropTypes.func.isRequired,

  // その他バインドされる情報
  classes: PropTypes.object.isRequired,
  reset: PropTypes.func.isRequired,
  handleSubmit: PropTypes.func.isRequired,
  history: PropTypes.object.isRequired,
  showMessage: PropTypes.func,
  values: PropTypes.object,
};

const FORM_NAME = 'DrSideKoziCostList';

const FormedTemplate = reduxForm({
  form: FORM_NAME,
  destroyOnUnmount: false,
  enableReinitialize: false,
})(connect((state) => {
  return {
    values: getFormValues(FORM_NAME)(state),
  };
})(Template));

export default withStyles(styles)(
    connect(
        mapStateToProps,
        mapDispatchToProps,
    )(FormedTemplate),
);
